import { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const SLIDES = [
  {
    url: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1800&q=80",
    label: "Structural Excellence",
  },
  {
    url: "https://plus.unsplash.com/premium_photo-1681989486976-9ec9d2eac57a?w=1800&q=80",
    label: "Precision Engineering",
  },
  {
    url: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1800&q=80",
    label: "Landmark Projects",
  },
];

const SLIDE_DURATION = 5000;

export default function HeroSection({ companyInfo }) {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      const next = (current + 1) % SLIDES.length;
      setPrev(current);
      setCurrent(next);
      setTimeout(() => setPrev(null), 1200);
    }, SLIDE_DURATION);
    return () => clearInterval(timer);
  }, [current]);

  const goTo = (idx) => {
    if (idx === current) return;
    setPrev(current);
    setCurrent(idx);
    setTimeout(() => setPrev(null), 1200);
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">

      <style>{`
        @keyframes growWidth  { from { width:0% } to { width:100% } }
        @keyframes fadeUp     { from { opacity:0; transform:translateY(22px) } to { opacity:1; transform:translateY(0) } }
        @keyframes scrollDrop {
          0%   { transform:scaleY(0); transform-origin:top;    opacity:1 }
          50%  { transform:scaleY(1); transform-origin:top;    opacity:1 }
          100% { transform:scaleY(1); transform-origin:bottom; opacity:0 }
        }
        .fu-1 { animation: fadeUp .7s ease .10s both }
        .fu-2 { animation: fadeUp .7s ease .25s both }
        .fu-3 { animation: fadeUp .7s ease .38s both }
        .fu-4 { animation: fadeUp .7s ease .50s both }
        .fu-5 { animation: fadeUp .7s ease .64s both }
        .scroll-anim { animation: scrollDrop 2s ease infinite }
        .progress-anim { animation: growWidth ${SLIDE_DURATION}ms linear forwards }
      `}</style>

      {SLIDES.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 bg-cover bg-center transition-opacity duration-[1200ms] ease-in-out
            ${i === current ? "opacity-100 z-10 scale-105" : ""}
            ${i === prev    ? "opacity-0  z-20"            : ""}
            ${i !== current && i !== prev ? "opacity-0 z-0" : ""}
          `}
          style={{ backgroundImage: `url(${slide.url})` }}
        />
      ))}

      <div className="absolute inset-0 z-30 bg-gradient-to-r from-slate-950/95 via-slate-950/70 to-slate-900/25 pointer-events-none" />
      <div className="absolute inset-0 z-30 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent pointer-events-none" />

      <div
        key={`pb-${current}`}
        className="progress-anim absolute bottom-0 left-0 h-[3px] bg-blue-500 z-50"
      />

      <div className="relative z-40 w-full px-6 sm:px-14 lg:px-24">
        <div className="max-w-3xl mt-24 md:mt-32">

          <div className="fu-1 inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-blue-400/30 bg-blue-500/10 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse flex-shrink-0" />
            <span className="text-blue-300 text-xs font-semibold tracking-[0.15em] uppercase">
              Building Excellence Since 1998
            </span>
          </div>

          <h1 className="font-black text-white leading-[0.93] tracking-tight mb-3"
              style={{ fontSize: "clamp(2rem,8.5vw,5.5rem)" }}>
            Constructing Your
            <span
              className="block text-transparent"
              style={{ WebkitTextStroke: "2px #3b82f6" }}
            >
              Future Today
            </span>
          </h1>

          <div className="fu-3 flex items-center gap-3 mb-5">
            <span className="w-10 h-px bg-blue-500 flex-shrink-0" />
            <span className="text-blue-400 text-[11px] font-bold tracking-[0.18em] uppercase">
              Premier UK Construction Services
            </span>
          </div>

          {/* Body copy */}
          <p className="fu-4 text-slate-300/80 font-light leading-relaxed max-w-xl mb-9"
             style={{ fontSize: "clamp(0.95rem,1.5vw,1.15rem)" }}>
            {companyInfo?.tagline ?? "Delivering landmark projects with precision and integrity."}{" "}
            Building lasting structures across the UK with over two decades of unmatched expertise.
          </p>

          {/* CTAs */}
          <div className="fu-5 flex flex-wrap gap-4">
            <button className="group inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-semibold text-sm tracking-wide px-8 py-3.5 rounded-md shadow-lg hover:shadow-blue-600/40 hover:shadow-xl transition-all duration-200">
              Start Your Project
              <ArrowRight
                size={17}
                className="group-hover:translate-x-1 transition-transform duration-200"
              />
            </button>
            <Link to="/contact">
              <button className="inline-flex items-center gap-2 border border-white/30 hover:border-white/60 hover:bg-white/10 active:scale-95 text-white font-medium text-sm tracking-wide px-8 py-3.5 rounded-md backdrop-blur-sm transition-all duration-200">
                Contact Us
              </button>
            </Link>
          </div>
        </div>
      </div>



      <div className="hidden sm:flex absolute right-8 top-1/2 -translate-y-1/2 z-40 flex-col gap-3 items-center">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-1 rounded-full transition-all duration-500 focus:outline-none
              ${i === current
                ? "h-12 bg-blue-500"
                : "h-7 bg-white/25 hover:bg-white/50"
              }`}
          />
        ))}
      </div>

      <div
        className="hidden lg:block absolute right-16 top-10 z-40 text-slate-400/50 text-[10px] font-semibold tracking-[0.22em] uppercase transition-all duration-500"
        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
      >
        {SLIDES[current].label}
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-2">
        <span className="text-white/25 text-[9px] tracking-[0.22em] uppercase font-semibold">
          Scroll
        </span>
        <div className="scroll-anim w-px h-10 bg-gradient-to-b from-white/40 to-transparent" />
      </div>

    </section>
  );
}